import React, { useCallback, useEffect, useState, useContext } from 'react'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import { getDailyTicketCounts } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import {
  checkPermission,
  createWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  saveWorkbook,
  numberFormat,
  formatShortDate,
  getTimezoneDate,
  timezones,
} from '../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import DailyTicketsCountReport from './DailyTicketsCountReport'
import { EmptyContainer } from '../EmptyContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'

export default function DailyTicketsCountReportWrapper({ organization }) {
  const { showLoading, hideLoading, isLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_TICKET_COUNTS
    )

  const [data, setData] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const orgUuid = organization?.uuid

  useEffect(() => {
    if (!orgUuid) return
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    showLoading()
    setIsLoadingData(true)
    getDailyTicketCounts(orgUuid)
      .then((res) => {
        setData(res.data)
        hideLoading()
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
      .finally(() => {
        setIsLoadingData(false)
        completeRefresh()
      })
  }, [orgUuid, orgPermissionsReady, hasPermission, refreshToken])

  const handleExcelExport = useCallback(() => {
    if (!orgPermissionsReady || !hasPermission) return
    if (!data?.length) return

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Daily tickets count', workbook)

    setHeaders('Daily Tickets Count', organization?.name || '', worksheet, [])

    const keys = [
      'event_name',
      'event_date',
      'event_status',
      'sold_yesterday',
      'sold_today',
      'total_sold',
      'comps',
      'packages',
      'total_allocated',
      'capacity',
    ]

    const formatRow = (d) => {
      const tz = timezones(d?.event?.venue?.[0]?.timezone)
      const start = d?.event?.start
      const eventDate =
        start && tz
          ? formatShortDate(getTimezoneDate(start, tz), 'dateOnly')
          : ''

      return {
        event_name: d?.event?.name ?? '',
        event_date: eventDate,
        event_status: 'On Sale',
        sold_yesterday: Number(d?.soldYesterday) || 0,
        sold_today: Number(d?.soldToday) || 0,
        total_sold: Number(d?.totalSold) || 0,
        comps: Number(d?.comps) || 0,
        packages: Number(d?.package) || 0,
        total_allocated: Number(d?.totalAllocated) || 0,
        capacity: Number(d?.capacity) || 0,
      }
    }

    const dataHeaderRow = worksheet.lastRow.number + 1
    setColumnsAndRows(keys, data, formatRow, worksheet)

    for (let col = 4; col <= 10; col += 1) {
      numberFormat(col, worksheet, dataHeaderRow)
    }

    saveWorkbook(workbook, 'daily tickets count report')
      .then()
      .catch((err) => alert(err.message))
  }, [data, organization?.name, orgPermissionsReady, hasPermission])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section
      className="spacer-sm"
      data-testid="daily-ticket-counts-report-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Daily tickets count</h1>
        </div>
        <p className="section-header-desc">
          This report summarizes daily ticket sales activity, including total
          tickets sold, comps, packages, and event capacity for each event.
        </p>
      </header>
      <Results
        data={data}
        onExportExcel={handleExcelExport}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
      />
    </section>
  )
}

function Results({ data, onExportExcel, onRefresh, isRefreshing }) {
  if (data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No data</p>
          <p className="subtitle">There's currently no data to show.</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <Stack direction="horizontal" gap={3} className="mb-4 align-items-end">
        <ReportRefreshButton
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
          disabled={isRefreshing}
        />
        <Button
          variant="outline-light"
          className="icon-button btn-download form-control-height"
          disabled={!data?.length}
          onClick={onExportExcel}
        >
          Excel
        </Button>
      </Stack>
      <DailyTicketsCountReport data={data} />
    </>
  )
}
